import isNumber from 'lodash/isNumber';
import React, { Component } from 'react';
import UI from '../ui';

const {
	WrappedButton,
	WrappedButtonType,
	WrappedDropdownWithLabel,
	WrappedInputTextWithLabel,
} = UI;

// 500 ms change buffer
const INPUT_CHANGE_BUFFER = 500;

export class Pagination extends Component {
	constructor(props) {
		super(props);

		const { currentPage = 1, pageSize, totalPages } = props;

		this.state = {
			// current displaying page
			currentPage,

			// this is used to restore the page for entering an invalid page number in the input field
			previousPage: currentPage,

			// page size option
			pageSize,

			// total pages
			totalPages,

			isFirstButtonDisabled: true,
			isPrevButtonDisabled: true,
			isNextButtonDisabled: true,
			isLastButtonDisabled: true,
		};

		// input buffer for current page input field
		this.inputChangeBuffer = null;

		// console.log(totalPages);
	}

	componentDidUpdate(prevProps) {
		const { currentPage, totalPages } = this.props;

		if (
			prevProps.currentPage !== currentPage ||
			prevProps.totalPages !== totalPages
		) {
			this.refreshPagination();
		}
	}

	// first page button click handler
	onFirstButtonClick = () => {
		const firstPage = 1;
		this.setCurrentPage(firstPage);
	};

	// prev page button click handler
	onPrevButtonClick = () => {
		const { currentPage } = this.state;
		const newPage = currentPage - 1;

		if (this.isPageValid(newPage)) {
			this.setCurrentPage(newPage);
		}
	};

	// next page button click handler
	onNextButtonClick = () => {
		const { currentPage } = this.state;
		const newPage = currentPage + 1;

		if (this.isPageValid(newPage)) {
			this.setCurrentPage(newPage);
		}
	};

	// next page button click handler
	onLastButtonClick = () => {
		const { totalPages } = this.state;

		if (this.isPageValid(totalPages)) {
			this.setCurrentPage(totalPages);
		}
	};

	// page input text change handler
	onInputTextChange = event => {
		const textValue = event.target.value;
		const newPage = textValue !== '' ? parseInt(textValue) : textValue;

		this.setState({ currentPage: newPage }, () => {
			// clear input timer
			if (this.inputChangeBuffer) {
				clearTimeout(this.inputChangeBuffer);
				this.inputChangeBuffer = null;
			}

			// trigger when user stops typing
			this.inputChangeBuffer = setTimeout(() => {
				if (this.isPageValid(newPage)) {
					this.setCurrentPage(newPage);
				}
			}, INPUT_CHANGE_BUFFER);
		});
	};

	// page input blur handler
	onInputTextBlur = event => {
		const textValue = event.target.value;
		const newPage = textValue !== '' ? parseInt(textValue) : textValue;
		const { previousPage } = this.state;

		// restore the current page to previous valid page
		if (!this.isPageValid(newPage)) {
			this.setCurrentPage(previousPage);
		}
	};

	// page size select handler
	onPageSizeChange = event => {
		const pageSize = parseInt(event.value);

		this.setPageSize(pageSize);
	};

	// set current page and previous page and notify the parent
	setCurrentPage(currentPage) {
		const { onPaginationChange } = this.props;
		const { pageSize } = this.state;

		this.setState(
			{
				currentPage,
				previousPage: currentPage,
			},
			() => {
				// update the buttons' state
				this.refreshPagination();
				// notify the parent about the change
				onPaginationChange(currentPage, pageSize);
			}
		);
	}

	// set the page size option and reset the current page to 1
	setPageSize(pageSize) {
		const currentPage = 1; // reset current page
		const { onPaginationChange } = this.props;

		this.setState({ currentPage, pageSize }, () => {
			// update the buttons' state
			this.refreshPagination();
			// notify the parent about the change
			onPaginationChange(currentPage, pageSize);
		});
	}

	// return true if  the page is within the valid range
	isPageValid(page) {
		const { totalPages } = this.props;

		return (
			isNumber(page) && isNumber(totalPages) && page >= 1 && page <= totalPages
		);
	}

	// refresh the state of 'First', 'Prev', 'Next', and 'Last' button
	refreshPagination() {
		const { currentPage, totalPages } = this.props;
		const totalPagesCount = isNumber(totalPages) ? totalPages : 0;

		this.setState({
			currentPage,
			totalPages: totalPagesCount > 0 ? totalPages : null,
			isFirstButtonDisabled: currentPage === 1,
			isPrevButtonDisabled: currentPage === 1,
			isNextButtonDisabled:
				totalPagesCount <= 1 || currentPage === totalPagesCount,
			isLastButtonDisabled:
				totalPagesCount <= 1 || currentPage === totalPagesCount,
		});
	}

	render() {
		const {
			currentPage,
			pageSize,
			totalPages,
			isFirstButtonDisabled,
			isPrevButtonDisabled,
			isNextButtonDisabled,
			isLastButtonDisabled,
		} = this.state;
		const {
			pageSizeOptions = [
				{ label: '100', value: 100 },
				{ label: '250', value: 250 },
				{ label: '500', value: 500 },
			],
			dataqa,
		} = this.props;

		return (
			<div className="common-grid-pagination">
				<div className="pagination-toolbar">
					<WrappedButton
						icon="pi pi-angle-double-left"
						className="pagination-button"
						buttonType={WrappedButtonType.TertiaryGrey}
						disabled={isFirstButtonDisabled}
						onClick={this.onFirstButtonClick}
						dataqa={`${dataqa}-pagination-first-btn`}
					/>
					<WrappedButton
						icon="pi pi-angle-left"
						className="pagination-button"
						buttonType={WrappedButtonType.TertiaryGrey}
						disabled={isPrevButtonDisabled}
						onClick={this.onPrevButtonClick}
						dataqa={`${dataqa}-pagination-prev-btn`}
					/>
					<WrappedInputTextWithLabel
						label="Page"
						className="pagination-input"
						uiElementConfig={{
							onChange: this.onInputTextChange,
							onBlur: this.onInputTextBlur,
							value: currentPage,
							dataqa: `${dataqa}-pagination-page-input`,
							keyfilter: 'pint',
						}}
					/>
					<div
						className="pagination-text"
						data-qa={`${dataqa}-pagination-total-page`}
						{...{ dataqa: `${dataqa}-pagination-total-page` }}
					>
						of {totalPages}
					</div>
					<WrappedButton
						icon="pi pi-angle-right"
						className="pagination-button"
						buttonType={WrappedButtonType.TertiaryGrey}
						disabled={isNextButtonDisabled}
						onClick={this.onNextButtonClick}
						dataqa={`${dataqa}-pagination-next-btn`}
					/>
					<WrappedButton
						icon="pi pi-angle-double-right"
						className="pagination-button"
						buttonType={WrappedButtonType.TertiaryGrey}
						disabled={isLastButtonDisabled}
						onClick={this.onLastButtonClick}
						dataqa={`${dataqa}-pagination-last-btn`}
					/>
				</div>

				{/* items after this component will be aligned to the right */}
				<div className="pagination-spacing" />

				<WrappedDropdownWithLabel
					className="pagination-dropdown"
					label="Page Size:"
					uiElementConfig={{
						id: 'common-grid-pagination-pagesize',
						dataqa: 'pagesize',
						value: pageSize,
						options: pageSizeOptions,
						optionLabel: 'label',
						optionValue: 'value',
						onChange: this.onPageSizeChange,
						placeholder: 'page size',
					}}
				/>
			</div>
		);
	}
}
